import { createContext, useContext, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import { canUseDOM } from 'exenv';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ESC_KEY = 27;
export default function ModalComponent(_ref) {
  let {
    className,
    children = null,
    isOpen = false,
    onCloseHandler = () => {},
    pauseFocus: _pauseFocus,
    canCloseFromOutside = true,
    type = 'default',
    ...focusTrapProps
  } = _ref;
  let modalRootRef = useRef(null);
  let wrapRef = useRef(null);
  let containerRef = useRef(null);
  let contentRef = useRef(null);
  let bgRef = useRef(null);
  const onCloseHandlerRef = useRef(onCloseHandler);
  const [el] = useState(() => {
    if (canUseDOM) {
      return document.createElement('div');
    }
    return null;
  });
  useEffect(function handler() {
    onCloseHandlerRef.current = onCloseHandler;
  }, [onCloseHandler]);
  useEffect(() => {
    if (canUseDOM) {
      const rootRef = document.querySelector('.modal-root');
      if (!rootRef) {
        const el = document.createElement('div');
        el.classList.add('modal-root');
        document.body.appendChild(el);
        modalRootRef.current = el;
      } else {
        modalRootRef.current = rootRef;
      }
    }
  });
  useEffect(function eventListenerEffect() {
    if (isOpen) {
      document.addEventListener('keydown', keyPressHandler);
    } else {
      document.removeEventListener('keydown', keyPressHandler);
    }
    function keyPressHandler(e) {
      if (e.keyCode === ESC_KEY) {
        if (isOpen) {
          onCloseHandlerRef.current();
        }
      }
    }
    return function clean() {
      document.removeEventListener('keydown', keyPressHandler);
    };
  }, [isOpen]);
  useEffect(() => {
    if (canUseDOM && el) {
      modalRootRef.current?.appendChild(el);
    }
    return () => {
      if (canUseDOM && el) {
        modalRootRef.current?.removeChild(el);
      }
    };
  }, [el]);
  useEffect(() => {
    if (isOpen) {
      if (canUseDOM && document.body.parentElement) {
        document.body.parentElement.classList.add('modal-open');
      }
    }
    return () => {
      if (canUseDOM && document.body.parentElement) {
        document.body.parentElement.classList.remove('modal-open');
      }
    };
  }, [isOpen]);
  function onExternalCloseHandler(e) {
    if (!canCloseFromOutside) {
      return;
    }
    const {
      target
    } = e;
    if (typeof onCloseHandler === 'function' && (target === wrapRef.current || target === containerRef.current || target === contentRef.current || target === bgRef.current)) {
      onCloseHandler(e);
    }
  }
  return isOpen && canUseDOM && el ? /*#__PURE__*/createPortal(/*#__PURE__*/_jsx(ModalContext.Provider, {
    value: {
      isOpen,
      onCloseHandler
    },
    children: /*#__PURE__*/_jsx(FocusTrap, {
      ...focusTrapProps,
      children: /*#__PURE__*/_jsxs("div", {
        className: cx('modal__outer', className, {
          'modal--image': type === 'image'
        }),
        onClick: onExternalCloseHandler,
        children: [/*#__PURE__*/_jsx("div", {
          ref: bgRef,
          className: cx('modal__bg', {
            'modal--image__bg': type === 'image'
          })
        }), /*#__PURE__*/_jsx("div", {
          ref: wrapRef,
          className: cx('modal__wrap', {
            'modal--image__wrap': type === 'image'
          }),
          children: /*#__PURE__*/_jsx("div", {
            ref: containerRef,
            className: cx('modal__container', {
              'modal--image__container': type === 'image'
            }),
            children: /*#__PURE__*/_jsxs("div", {
              ref: contentRef,
              className: cx('modal__content', {
                'modal--image__content': type === 'image'
              }),
              children: [/*#__PURE__*/_jsx("div", {
                tabIndex: -1,
                className: cx('mftfallback')
              }), children]
            })
          })
        })]
      })
    })
  }), el) : null;
}
const ModalContext = /*#__PURE__*/createContext(undefined);
export function useModalContext() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used within a ModalContextProvider');
  }
  return context;
}